import { billingOrganizationContract } from "@common/contracts/billing-organization.contract";
import { getBackendAPIClient } from "@lib/backendAPIClient";
import { useMutation, useQuery } from "@tanstack/react-query";
import { queryClient } from "queryClient";
import { Organization } from "@common/types/organization";
import {
    BillingDtoSchema,
    BillingHistoryDtoSchema,
    BillingInfoDtoSchema,
    OrganizationOwnedPlanPricesResponseSchema,
    OrganizationPlansResponseSchema,
    UpcomingInvoiceDtoSchema,
} from "@common/schemas/billing.schema";
import { OrganizationAdminQuantitiesExplainResponseSchema } from "@common/schemas/organization-admin.schema";
import { organizationQueryKeys } from "./organization.api";

const billingOrganizationContractClient = getBackendAPIClient(
    billingOrganizationContract,
);

export const billingOrganizationQueryKeys = {
    all: (organizationId: string) => ["billing-organization", organizationId],
    billingInfo: (organizationId: string) => [
        ...billingOrganizationQueryKeys.all(organizationId),
        "billing-info",
    ],
    upcomingInvoice: (organizationId: string) => [
        ...billingOrganizationQueryKeys.all(organizationId),
        "upcoming-invoice",
    ],
    quantitiesExplain: (organizationId: string) => [
        ...billingOrganizationQueryKeys.all(organizationId),
        "quantities-explain",
    ],
    invoices: (organizationId: string) => [
        ...billingOrganizationQueryKeys.all(organizationId),
        "invoices",
    ],
    plans: (organizationId: string) => [
        ...billingOrganizationQueryKeys.all(organizationId),
        "plans",
    ],
    organizationOwnedPlanPrices: (organizationId: string) => [
        ...billingOrganizationQueryKeys.all(organizationId),
        "organization-owned-plan-prices",
    ],
} as const;

export function useOrganizationBillingInfoQuery(
    organizationId: string | null | undefined,
) {
    return useQuery({
        queryKey: billingOrganizationQueryKeys.billingInfo(
            organizationId ?? "",
        ),
        enabled: !!organizationId,
        queryFn: async () => {
            const response =
                await billingOrganizationContractClient.getBillingInfo({
                    params: { organizationId: organizationId ?? "" },
                });

            return response.body
                ? BillingInfoDtoSchema.parse(response.body)
                : null;
        },
    });
}

export function useOrganizationUpcomingInvoiceQuery(
    organizationId: string | null | undefined,
    enabled: boolean = true,
) {
    return useQuery({
        queryKey: billingOrganizationQueryKeys.upcomingInvoice(
            organizationId ?? "",
        ),
        enabled: !!organizationId && enabled,
        queryFn: async () => {
            const response =
                await billingOrganizationContractClient.getUpcomingInvoice({
                    params: { organizationId: organizationId ?? "" },
                });

            return UpcomingInvoiceDtoSchema.parse(response.body);
        },
    });
}

export function useOrganizationInvoicesQuery(
    organizationId: string | null | undefined,
    enabled: boolean = true,
) {
    return useQuery({
        queryKey: billingOrganizationQueryKeys.invoices(organizationId ?? ""),
        enabled: !!organizationId && enabled,
        queryFn: async () => {
            const response =
                await billingOrganizationContractClient.getInvoices({
                    params: { organizationId: organizationId ?? "" },
                });

            return BillingHistoryDtoSchema.parse(response.body);
        },
    });
}

export function useOrganizationQuantitiesExplainQuery(
    organizationId: string | null | undefined,
    enabled: boolean = true,
) {
    return useQuery({
        queryKey: billingOrganizationQueryKeys.quantitiesExplain(
            organizationId ?? "",
        ),
        enabled: !!organizationId && enabled,
        queryFn: async () => {
            const response =
                await billingOrganizationContractClient.getQuantitiesExplain({
                    params: { organizationId: organizationId ?? "" },
                    query: {},
                });

            return OrganizationAdminQuantitiesExplainResponseSchema.parse(
                response.body,
            );
        },
    });
}

export function useOrganizationPlansQuery(
    organizationId: string | null | undefined,
) {
    return useQuery({
        queryKey: billingOrganizationQueryKeys.plans(organizationId ?? ""),
        enabled: !!organizationId,
        queryFn: async () => {
            const response = await billingOrganizationContractClient.getPlans({
                params: { organizationId: organizationId ?? "" },
            });

            return OrganizationPlansResponseSchema.parse(response.body);
        },
    });
}

export function useOrganizationOwnedPlanPricesQuery(
    organizationId: string | null | undefined,
) {
    return useQuery({
        queryKey: billingOrganizationQueryKeys.organizationOwnedPlanPrices(
            organizationId ?? "",
        ),
        enabled: !!organizationId,
        queryFn: async () => {
            const response =
                await billingOrganizationContractClient.getOrganizationOwnedPlanPrices(
                    {
                        params: { organizationId: organizationId ?? "" },
                    },
                );

            return OrganizationOwnedPlanPricesResponseSchema.parse(
                response.body,
            );
        },
    });
}

export function useEnrollOrganizationMutation(organizationId: string) {
    return useMutation({
        mutationFn: async (setupIntentId: string) => {
            const response = await billingOrganizationContractClient.enroll({
                params: { organizationId },
                body: { setupIntentId },
            });
            return BillingDtoSchema.parse(response.body);
        },
        onSuccess: async (billing) => {
            await queryClient.setQueryData(
                organizationQueryKeys.get(),
                (old: Organization | null) => {
                    if (!old) {
                        return null;
                    }

                    return { ...old, billing };
                },
            );
            await queryClient.invalidateQueries({
                queryKey: organizationQueryKeys.all(),
            });
        },
    });
}
